import type { UserManual } from "./types.js";

export const BASIC_TUTORIAL: UserManual = {
  audience: "Students and hobbyists who have never used BASIC before and want a patient path from one-line experiments to complete little programs in the resident BASIC environment.",
  description:
    "A progressive beginner course for the AM8 BASIC dialect. The lessons cover the editor workflow, every shipped BASIC language construct, key built-in functions, and the practical habits that make early BASIC programming enjoyable instead of confusing.",
  eyebrow: "BASIC Tutorial",
  highlights: ["Tutorial | 14 lessons", "Focus | every shipped BASIC construct in context", "Outcome | write, edit, run, and extend real BASIC programs"],
  id: "basic-tutorial",
  title: "BASIC Programming for Beginners",
  sections: [
    {
      id: "basic-lesson-1",
      summary: "Enter BASIC mode, understand immediate execution, and write the classic first program in both direct and stored form.",
      title: "Lesson 1: Enter BASIC and Print Hello, world!",
      bodyHtml: `
        <p>BASIC is one of the friendliest first languages because it gives feedback fast. You can type one line, press return, and see a visible result immediately. That fast loop is ideal for beginners who are still learning what a programming language even feels like.</p>
        <p>Start by switching the shell into BASIC mode:</p>
        <pre><code>BASIC</code></pre>
        <p>The prompt changes to <code>B&gt;</code>. In BASIC mode, <strong>unnumbered input runs immediately</strong>, while <strong>numbered input is stored as part of a program</strong>. That distinction is the foundation of the whole workflow.</p>
        <p>First try an immediate command:</p>
        <pre><code>PRINT "Hello, world!"</code></pre>
        <p>This runs right away. Nothing is stored yet. Now turn the same idea into a real program:</p>
        <pre><code>10 PRINT "Hello, world!"
20 END</code></pre>
        <p>Then type:</p>
        <pre><code>RUN</code></pre>
        <p>You have already learned three essential ideas:</p>
        <ul>
          <li><code>BASIC</code> enters the language mode.</li>
          <li><code>PRINT</code> shows output.</li>
          <li><code>END</code> marks a clean program end.</li>
        </ul>
        <p>That may seem small, but it is the complete beginner loop: enter the language, write a line, run it, see the result.</p>
      `
    },
    {
      id: "basic-lesson-2",
      summary: "Use BASIC like a calculator and learn the expression surface before embedding it into larger programs.",
      title: "Lesson 2: Immediate Mode, Expressions, and Operators",
      bodyHtml: `
        <p>Immediate mode is one of BASIC's greatest teaching tools because it lets you test ideas without building a whole program around them first. Use it as a calculator and as an expression laboratory.</p>
        <p>Try these lines individually:</p>
        <pre><code>PRINT 2+3
PRINT 7*8
PRINT (10+5)/3
PRINT 13 MOD 5
PRINT $20 + 5
PRINT 3&lt;4
PRINT 5&gt;=5
PRINT 7#2
PRINT NOT 0
PRINT 6 AND 3
PRINT 6 OR 3
PRINT 6 XOR 3</code></pre>
        <p>This lesson covers most of the expression building blocks you will use throughout the tutorial:</p>
        <ul>
          <li>Arithmetic: <code>+</code>, <code>-</code>, <code>*</code>, <code>/</code>, and <code>MOD</code>.</li>
          <li>Grouping with parentheses.</li>
          <li>Hexadecimal literals with the <code>$</code> prefix.</li>
          <li>Comparisons such as <code>=</code>, <code>&lt;</code>, <code>&gt;</code>, <code>&lt;=</code>, <code>&gt;=</code>, and <code>#</code> for not-equal.</li>
          <li>Bitwise logic with <code>NOT</code>, <code>AND</code>, <code>OR</code>, and <code>XOR</code>.</li>
        </ul>
        <p>The key beginner habit is simple: test expressions first, then place them inside programs. That habit makes every later lesson easier.</p>
      `
    },
    {
      id: "basic-lesson-3",
      summary: "Store results in variables and learn why named state is easier to reason about than repeating raw expressions everywhere.",
      title: "Lesson 3: Variables, LET, and Assignment",
      bodyHtml: `
        <p>BASIC becomes more useful once you stop working only with literal numbers and start storing results. In this dialect, variables are single letters <code>A</code> through <code>Z</code>.</p>
        <p>Try these commands:</p>
        <pre><code>LET A=42
PRINT A
B=A+8
PRINT B
C=7*9
PRINT C</code></pre>
        <p><code>LET</code> is optional. Both <code>LET A=42</code> and <code>A=42</code> mean assignment. Early on, many learners prefer <code>LET</code> because it reads more like plain English.</p>
        <p>Variables matter because they give a value a role. A number can become a score, a width, a turn counter, or a player guess. That makes programs easier to read and easier to change later.</p>
        <p>Compare these two styles:</p>
        <pre><code>PRINT (5+7)*(5+7)

X=5+7
PRINT X*X</code></pre>
        <p>The second version is easier to explain to another person and easier to debug. Good programming is not only about satisfying the interpreter. It is also about organizing your own thinking.</p>
      `
    },
    {
      id: "basic-lesson-4",
      summary: "Learn how stored BASIC programs are created, listed, replaced, deleted, cleared, and exited cleanly.",
      title: "Lesson 4: Program Editing with Lines, LIST, RUN, NEW, and EXIT",
      bodyHtml: `
        <p>Classic BASIC programs are built from numbered lines. Each line number works both as an editing handle and as a possible control-flow target later.</p>
        <p>Enter this program:</p>
        <pre><code>10 PRINT "FIRST LINE"
20 PRINT "SECOND LINE"
30 END</code></pre>
        <p>Now inspect it with:</p>
        <pre><code>LIST</code></pre>
        <p>Run it with:</p>
        <pre><code>RUN</code></pre>
        <p>Replace one line by typing the same line number again:</p>
        <pre><code>20 PRINT "SECOND LINE UPDATED"</code></pre>
        <p>You can also edit explicitly with the command form:</p>
        <pre><code>LINE 100 PRINT "EXPLICIT EDIT"
LINE 100</code></pre>
        <p>The first form creates or replaces line 100. The second form deletes it.</p>
        <p>Use <code>NEW</code> when you want to clear the current stored BASIC program. Use <code>EXIT</code> when you want to leave BASIC mode and return to the plain shell prompt.</p>
        <p>When a small experiment becomes worth keeping, the shell-level disk workflow is ready too:</p>
        <pre><code>SAVE "HELLO" OVERWRITE
LOAD "HELLO"</code></pre>
        <p>While BASIC mode is active, <code>SAVE</code> can store the current shared program image without making you calculate raw memory ranges first. Later, <code>LOAD</code> restores that saved image so you can continue editing or run it again.</p>
        <p>This lesson is about workflow, not only syntax. BASIC rewards people who understand how to inspect and revise a program as they build it.</p>
      `
    },
    {
      id: "basic-lesson-5",
      summary: "Shape readable output and learn how PRINT zones, direct concatenation, and semicolon chaining turn one line into a clear sequence of actions.",
      title: "Lesson 5: PRINT, Zones, Commas, and Chaining",
      bodyHtml: `
        <p>Output is how early BASIC programs communicate, and small formatting choices matter. The same data can feel clumsy or friendly depending on how you print it.</p>
        <pre><code>PRINT "SCORE:",12
PRINT "X=",3," Y=",7
PRINT 1:2:3
PRINT ::2
PRINT "HELLO"," AGAIN"</code></pre>
        <p>Important rules:</p>
        <ul>
          <li>A colon inside <code>PRINT</code> jumps to the next 10-column output zone. Repeated colons can skip multiple zones, as in <code>PRINT 1::2</code> or <code>PRINT ::2</code>.</li>
          <li>If a colon tries to advance past the last zone, BASIC starts a fresh output line instead of wrapping awkwardly inside the same one.</li>
          <li>A comma inside <code>PRINT</code> concatenates items directly, so spaces should be included inside strings when you want them.</li>
          <li>A semicolon is not a PRINT modifier here. It is the global BASIC statement separator for chaining actions on one line.</li>
        </ul>
        <p>BASIC also lets you chain multiple statements on one line with <code>;</code>. For example:</p>
        <pre><code>10 A=5; B=7; PRINT "SUM=",A+B
20 END</code></pre>
        <p>Chaining is useful for short related steps, but do not overuse it. A line packed with too many actions becomes hard to read. Use it to keep close ideas together, not to compress everything possible.</p>
      `
    },
    {
      id: "basic-lesson-6",
      summary: "Add comments and understand the statements that stop, end, or clear program flow so your program life cycle becomes predictable.",
      title: "Lesson 6: REM, END, STOP, and Program Lifecycle",
      bodyHtml: `
        <p>A growing program needs explanation and control. BASIC gives you this through comments and lifecycle statements.</p>
        <p>Use <code>REM</code> to turn the rest of the line into a comment:</p>
        <pre><code>10 REM THIS PROGRAM COUNTS UP
20 FOR I=1 TO 3
30 PRINT I
40 NEXT I
50 END</code></pre>
        <p>Use <code>END</code> when the program finishes normally. Use <code>STOP</code> when you want to halt early before later lines run:</p>
        <pre><code>10 INPUT "ENTER A NUMBER",A
20 IF A&lt;0 THEN PRINT "NEGATIVE NOT ALLOWED"; STOP
30 PRINT "YOU ENTERED ",A
40 END</code></pre>
        <p><code>NEW</code> clears the stored BASIC program, as you saw earlier. Together, <code>REM</code>, <code>END</code>, <code>STOP</code>, and <code>NEW</code> define much of the everyday program lifecycle.</p>
        <p>Good beginner habit: add short <code>REM</code> comments when the reason for a line is not obvious from the line itself. Comments are not decoration; they explain intention.</p>
      `
    },
    {
      id: "basic-lesson-7",
      summary: "Make BASIC interactive by asking the user for data and turning that input into calculations or later control-flow decisions.",
      title: "Lesson 7: INPUT and User Interaction",
      bodyHtml: `
        <p>Programs become much more interesting once they react to the user. In this BASIC, <code>INPUT</code> reads numeric values into variables or array cells.</p>
        <pre><code>10 INPUT "YOUR AGE",A
20 PRINT "NEXT YEAR YOU WILL BE ",A+1
30 END</code></pre>
        <p>You can read more than one value at once, and the targets may be variables or anonymous array cells:</p>
        <pre><code>10 INPUT "WIDTH,HEIGHT",W,H
20 PRINT "AREA=",W*H
30 END</code></pre>
        <pre><code>INPUT "FIRST TWO CELLS",@(0),@(1)</code></pre>
        <p>The entered numbers may be signed decimal values such as <code>-3</code> or <code>+7</code>, and they may also use the BASIC hex form such as <code>$2A</code>. BASIC still treats INPUT as numeric in this revision, so text entry is not part of the current surface.</p>
        <p>This simple pattern appears everywhere in programming:</p>
        <ol>
          <li>Ask for data.</li>
          <li>Store it.</li>
          <li>Transform it.</li>
          <li>Show the result.</li>
        </ol>
        <p>Make prompts clear. A short precise prompt usually prevents more confusion than a long vague one.</p>
      `
    },
    {
      id: "basic-lesson-8",
      summary: "Choose between alternatives with IF and ELSE while using comparison results as the basis for decisions.",
      title: "Lesson 8: IF, THEN, ELSE, and Decision Making",
      bodyHtml: `
        <p>A program without decisions is only a script. A program with decisions starts to behave differently depending on data. BASIC does this with <code>IF</code>, optionally <code>THEN</code>, and optionally <code>ELSE</code>.</p>
        <pre><code>10 INPUT "ENTER A NUMBER",A
20 IF A=0 THEN PRINT "ZERO" ELSE PRINT "NOT ZERO"
30 END</code></pre>
        <p>You can also write a shorter form without <code>THEN</code>:</p>
        <pre><code>IF A#2 PRINT "A IS NOT 2"</code></pre>
        <p>And when the branch target is simply another line, <code>THEN</code> or <code>ELSE</code> may jump there directly:</p>
        <pre><code>40 IF A=0 THEN 100 ELSE 200</code></pre>
        <p>That shorthand is useful because it keeps old-school line-number control flow short and readable. You can think of it as an implied <code>GOTO</code> target.</p>
        <p>Use comparisons to shape meaning:</p>
        <pre><code>10 INPUT "SCORE",S
20 IF S&gt;=90 THEN PRINT "EXCELLENT" ELSE IF S&gt;=50 THEN PRINT "PASS" ELSE PRINT "REVIEW"
30 END</code></pre>
        <p>The key conceptual shift is this: conditions are not only true or false facts. They are tools for choosing the next action.</p>
      `
    },
    {
      id: "basic-lesson-9",
      summary: "Repeat actions with counted loops and learn to think in terms of a changing loop variable instead of copied code.",
      title: "Lesson 9: FOR, STEP, and NEXT",
      bodyHtml: `
        <p>Repetition is one of the core powers of programming. In BASIC, counted repetition is handled by <code>FOR</code> and <code>NEXT</code>.</p>
        <pre><code>10 FOR I=1 TO 5
20 PRINT I
30 NEXT I
40 END</code></pre>
        <p>Optional <code>STEP</code> changes the increment:</p>
        <pre><code>10 FOR I=0 TO 20 STEP 5
20 PRINT I
30 NEXT I
40 END</code></pre>
        <p>Negative steps count down:</p>
        <pre><code>10 FOR I=10 TO 1 STEP -1
20 PRINT I
30 NEXT
40 PRINT "LIFT-OFF"
50 END</code></pre>
        <p>Notice that <code>NEXT</code> may name the loop variable or omit it when the current loop is obvious. As a beginner, naming it can make the flow easier to read.</p>
        <p>A good exercise is a multiplication table:</p>
        <pre><code>10 INPUT "TABLE FOR",N
20 FOR I=1 TO 10
30 PRINT N," X ",I," = ",N*I
40 NEXT I
50 END</code></pre>
      `
    },
    {
      id: "basic-lesson-10",
      summary: "Jump, dispatch, and return through numbered programs while learning to use those tools deliberately rather than chaotically.",
      title: "Lesson 10: GOTO, GOSUB, RETURN, and ON...GOTO / GOSUB",
      bodyHtml: `
        <p>Because BASIC programs are built from numbered lines, they can jump directly between them. This is useful for menus, reusable routines, and compact old-style control flow.</p>
        <p>The simplest jump is <code>GOTO</code>:</p>
        <pre><code>10 PRINT "START"
20 GOTO 40
30 PRINT "YOU WILL NOT SEE THIS"
40 PRINT "END"
50 END</code></pre>
        <p><code>GOSUB</code> calls a subroutine and expects a later <code>RETURN</code>:</p>
        <pre><code>10 PRINT "BEFORE"
20 GOSUB 100
30 PRINT "AFTER"
40 END
100 PRINT "INSIDE SUBROUTINE"
110 RETURN</code></pre>
        <p>Selector-based branching uses <code>ON ... GOTO</code> or <code>ON ... GOSUB</code>:</p>
        <pre><code>10 INPUT "CHOOSE 1-3",A
20 ON A GOTO 100,200,300
30 PRINT "NO MATCH"; END
100 PRINT "OPTION 1"; END
200 PRINT "OPTION 2"; END
300 PRINT "OPTION 3"; END</code></pre>
        <pre><code>10 INPUT "CHOOSE 1-2",A
20 ON A GOSUB 100,200
30 PRINT "BACK FROM SUBROUTINE"
40 END
100 PRINT "FIRST ROUTINE"; RETURN
200 PRINT "SECOND ROUTINE"; RETURN</code></pre>
        <p>These statements are powerful, but readability still matters. Use them to reveal structure, not to hide it.</p>
      `
    },
    {
      id: "basic-lesson-11",
      summary: "Separate logic from embedded data and start thinking in terms of tables instead of repeating constants throughout a program.",
      title: "Lesson 11: DATA, READ, and RESTORE",
      bodyHtml: `
        <p>Some programs need built-in tables of values. BASIC supports that directly through <code>DATA</code>, <code>READ</code>, and <code>RESTORE</code>.</p>
        <pre><code>10 DATA 7,9,13
20 READ A,B,C
30 PRINT A," ",B," ",C
40 END</code></pre>
        <p><code>READ</code> consumes the values in order. In this BASIC revision, <code>DATA</code> is numeric-only, so text items do not belong there. <code>RESTORE</code> rewinds the data cursor so reading can start from the beginning again:</p>
        <pre><code>10 DATA 2,4,6
20 READ A
30 PRINT A
40 READ B
50 PRINT B
60 RESTORE
70 READ C
80 PRINT C
90 END</code></pre>
        <p>This dialect's <code>RESTORE</code> has no line-number form. It always rewinds to the first available DATA item. That keeps the rule simple for beginners: one command, full rewind.</p>
        <p>This encourages a useful design habit: keep instructions and data conceptually separate. A good program does not always bury its important values inside the middle of its logic.</p>
      `
    },
    {
      id: "basic-lesson-12",
      summary: "Use anonymous array cells and helper functions so a BASIC program can remember more state and ask better questions about values and memory.",
      title: "Lesson 12: Arrays, ABS, RND, PEEK, and SIZE",
      bodyHtml: `
        <p>This BASIC includes anonymous array cells <code>@(0..31)</code> and several built-in functions that make programs more flexible.</p>
        <p>Array cells let you store indexed values:</p>
        <pre><code>10 FOR I=0 TO 4
20 @(I)=I*I
30 NEXT I
40 FOR I=0 TO 4
50 PRINT I," -> ",@(I)
60 NEXT I
70 END</code></pre>
        <p>Useful built-ins include:</p>
        <ul>
          <li><code>ABS(X)</code> for absolute value.</li>
          <li><code>RND(n)</code> for random-style values from <code>1</code> to <code>n</code>. A negative <code>n</code> reseeds from <code>abs(n)</code> first, and <code>RND(0)</code> is rejected instead of producing a value.</li>
          <li><code>PEEK(addr)</code> for reading one byte as part of an expression.</li>
          <li><code>SIZE</code> for asking about available space information in the environment.</li>
        </ul>
        <pre><code>10 INPUT "ENTER A NUMBER",A
20 PRINT "DISTANCE FROM ZERO = ",ABS(A)
30 PRINT "RANDOM EXAMPLE = ",RND(10)
40 PRINT "TOP-LEVEL SIZE VALUE = ",SIZE
50 PRINT "BYTE AT $0400 = ",PEEK($0400)
60 END</code></pre>
        <p><code>SIZE</code> is used as a zero-argument expression term rather than a parenthesized function call, so you write <code>PRINT SIZE</code>, not <code>PRINT SIZE()</code>.</p>
        <p>This lesson is where BASIC starts to feel broader than a line-based calculator. You now have indexed state, randomness, introspection, and memory reads inside expressions.</p>
      `
    },
    {
      id: "basic-lesson-13",
      summary: "Touch memory and machine-code entry points carefully so you understand the language's bridge into lower-level system programming.",
      title: "Lesson 13: POKE and USR for Machine Interaction",
      bodyHtml: `
        <p>BASIC on this system is not sealed away from the machine. It can also touch RAM and device space directly, which is exciting and dangerous in equal measure.</p>
        <p><code>POKE</code> writes one byte to a computed address:</p>
        <pre><code>10 POKE $0400,65
20 PRINT "WROTE A BYTE TO MEMORY"
30 END</code></pre>
        <p>That BASIC statement is separate from the shell-level <code>POKE</code> command, even though both write memory. Inside a BASIC program, <code>POKE</code> is just another BASIC statement.</p>
        <p><code>USR(expr)</code> is a more advanced bridge. It calls into a machine-code routine and returns a value. A conceptual example looks like this:</p>
        <pre><code>10 REM ASSUMES A MACHINE-CODE ROUTINE EXISTS AT $C000
20 PRINT USR($C000)
30 END</code></pre>
        <p>You do not need to use <code>USR()</code> in your first week with BASIC, but it is important to know that the language can call lower-level routines when you are ready. That makes BASIC a stronger systems-learning language than many beginners expect.</p>
      `
    },
    {
      id: "basic-lesson-14",
      summary: "Combine the core language features into one coherent little project and review the workflow that makes BASIC pleasant to use.",
      title: "Lesson 14: Mini Project - A Guessing Game with Comments and State",
      bodyHtml: `
        <p>Time to bring several ideas together. This small guessing game uses variables, comments, input, conditions, loops, output, and random number generation.</p>
        <pre><code>10 REM GUESS A NUMBER FROM 1 TO 10
20 PRINT "I AM THINKING OF A NUMBER FROM 1 TO 10"
30 N=RND(10)
40 FOR T=1 TO 5
50 INPUT "YOUR GUESS",G
60 IF G=N THEN PRINT "CORRECT!"; GOTO 130
70 IF G&lt;N THEN PRINT "TOO LOW" ELSE PRINT "TOO HIGH"
80 NEXT T
90 PRINT "OUT OF TURNS"
100 PRINT "THE NUMBER WAS ",N
110 END
130 PRINT "YOU NEEDED ",T," TURN(S)"
140 END</code></pre>
        <p>Now extend it:</p>
        <ol>
          <li>Add <code>@()</code> array cells to store previous guesses.</li>
          <li>Use <code>STOP</code> to halt early after an impossible input.</li>
          <li>Split your result formatting into a <code>GOSUB</code> routine.</li>
          <li>Replace one fixed rule with an <code>ON ... GOSUB</code> menu.</li>
        </ol>
        <p>At this point you have seen every major BASIC feature currently implemented in the system's tutorial and guide surface. The best next step is not more reading. It is repetition: write many tiny programs, revise them with <code>LIST</code> and <code>LINE</code>, and keep using immediate mode to test ideas before you formalize them.</p>
        <div class="manuals-callout">
          <p><strong>Coverage checklist:</strong> this tutorial now explicitly covers the shipped BASIC surface.</p>
          <ul>
            <li><strong>Workflow:</strong> entering BASIC mode, immediate execution, numbered line editing, <code>LIST</code>, <code>RUN</code>, <code>NEW</code>, <code>EXIT</code>, and the active-mode <code>SAVE</code>/<code>LOAD</code> disk flow.</li>
            <li><strong>Core statements:</strong> <code>PRINT</code>, <code>LET</code>, <code>INPUT</code>, <code>IF</code>/<code>THEN</code>/<code>ELSE</code>, <code>FOR</code>/<code>NEXT</code>, <code>GOTO</code>, <code>GOSUB</code>, <code>RETURN</code>, <code>ON ... GOTO</code>, <code>ON ... GOSUB</code>, <code>POKE</code>, <code>DATA</code>, <code>READ</code>, <code>RESTORE</code>, <code>REM</code>, <code>END</code>, <code>STOP</code>.</li>
            <li><strong>PRINT semantics:</strong> 10-column colon zones, repeated colon skipping, end-of-line zone rollover, comma concatenation, and global semicolon chaining.</li>
            <li><strong>Expression surface:</strong> variables <code>A..Z</code>, anonymous cells <code>@(0..31)</code>, unary <code>+</code>/<code>-</code>/<code>NOT</code>, binary <code>+</code>, <code>-</code>, <code>*</code>, <code>/</code>, <code>MOD</code>, <code>AND</code>, <code>OR</code>, <code>XOR</code>, relational operators <code>=</code>, <code>&lt;</code>, <code>&gt;</code>, <code>&lt;=</code>, <code>&gt;=</code>, <code>#</code>.</li>
            <li><strong>Literals and numeric rules:</strong> signed decimal input, <code>$</code>-prefixed hexadecimal, and signed 16-bit runtime semantics.</li>
            <li><strong>Built-ins:</strong> <code>ABS()</code>, <code>RND(expr)</code> with negative reseed and rejected zero, <code>PEEK()</code>, <code>USR()</code>, and zero-argument <code>SIZE</code>.</li>
            <li><strong>DATA/input rules:</strong> numeric-only <code>DATA</code>, full-rewind <code>RESTORE</code>, and numeric <code>INPUT</code> targets including <code>@()</code> cells.</li>
          </ul>
        </div>
        <div class="manuals-callout">
          <p><strong>Learning strategy:</strong> BASIC rewards short cycles. Type small examples, run them immediately, change one thing, and run again. That loop is one of the reasons BASIC remains such a strong teaching language.</p>
        </div>
      `
    }
  ]
};
